import { ProjectDot } from "@/components/projects/project-color";
import type { TimeEntriesResponse, TimeEntry } from "@/components/time-entries/list/types";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { DatePicker } from "@/components/ui/date-picker";
import { Pagination } from "@/components/ui/pagination";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatCzDate, isoToPragueDate, pragueWallToIso } from "@/lib/datetime";
import { projectHref } from "@/lib/project-href";
import { paginationSearchFields } from "@/lib/search";
import { formatHours } from "@/lib/utils";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

/**
 * `/time-entries` — **your own** time.
 *
 * The full list (`/time-entries-all`) exists to run the team's time: bulk
 * approvals, cross-user selection, billing states, the admin edit modal, the
 * discarded-triage lane. All of that is machinery for deciding about *other
 * people's* hours, and it buries the one question this page answers — what did
 * I log, and where did it land?
 *
 * So this is deliberately a fraction of it: your rows, two filters, no
 * selection, no bulk bars, nothing that mutates. Admins get it too, and see
 * exactly what everyone else sees — that's the point.
 */

const searchSchema = z.object({
  projectId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  ...paginationSearchFields,
});

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const APPROVAL_VARIANT: Record<string, "default" | "secondary" | "success" | "outline"> = {
  worked: "outline",
  budget: "secondary",
  approved: "success",
  paid: "default",
  rejected: "secondary",
};

export const Route = createFileRoute("/_app/time-entries/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    projectId: search.projectId,
    from: search.from,
    to: search.to,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: async ({ deps }) => {
    const qs = new URLSearchParams({ scope: "mine" });
    if (deps.projectId) qs.set("projectId", deps.projectId);
    if (deps.from) qs.set("from", deps.from);
    if (deps.to) qs.set("to", deps.to);
    if (deps.page) qs.set("page", String(deps.page));
    if (deps.pageSize) qs.set("pageSize", String(deps.pageSize));
    return api.get<TimeEntriesResponse>(`/api/time-entries?${qs.toString()}`);
  },
  component: MyTimeEntriesPage,
});

/** Same filters as the loader's fetch, for the "Download CSV" / "Download
 *  PDF" links — the export always matches exactly what's on screen. */
function pdfQuery(search: z.infer<typeof searchSchema>): string {
  const qs = new URLSearchParams({ scope: "mine" });
  if (search.projectId) qs.set("projectId", search.projectId);
  if (search.from) qs.set("from", search.from);
  if (search.to) qs.set("to", search.to);
  return qs.toString();
}

/** The project link, or null for a row whose project isn't GitLab-bound. */
function hrefFor(project: NonNullable<TimeEntry["project"]>): string | null {
  try {
    return projectHref(project);
  } catch {
    return null;
  }
}

function MyTimeEntriesPage() {
  const { entries, total, totalHours, page, pageSize, projects } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();

  function setFilter(patch: Partial<typeof search>) {
    // Any filter change returns to page 1 — staying on page 4 of a narrower
    // result is how you end up staring at an empty table.
    navigate({ to: "/time-entries", search: { ...search, ...patch, page: undefined } });
  }

  const columns: Array<DataTableColumn<TimeEntry>> = [
    {
      key: "spentOn",
      header: "Date",
      headClassName: "w-28",
      sortAccessor: (te) => te.spentOn,
      cell: (te) => <span className="text-sm">{formatCzDate(te.spentOn.slice(0, 10))}</span>,
    },
    {
      key: "project",
      header: "Project",
      headClassName: "w-52",
      sortAccessor: (te) => te.project?.name ?? te.externalProjectName ?? "",
      cell: (te) =>
        te.project ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <ProjectDot projectId={te.project.id} />
            {hrefFor(te.project) ? (
              <AppLink href={hrefFor(te.project) as string} className="truncate hover:underline">
                {te.project.name}
              </AppLink>
            ) : (
              <span className="truncate">{te.project.name}</span>
            )}
          </span>
        ) : (
          <span className="text-xs italic text-muted-foreground">
            {te.externalProjectName ? `Clockify: ${te.externalProjectName}` : "No project"}
          </span>
        ),
    },
    {
      key: "target",
      header: "Work item",
      cell: (te) =>
        te.workItem ? (
          <Link
            to="/work-item/$id"
            params={{ id: String(te.workItem.globalId) }}
            className="block truncate text-sm hover:underline"
          >
            {te.workItem.title}
          </Link>
        ) : te.overhead ? (
          <span className="text-sm text-muted-foreground">{te.overhead.name}</span>
        ) : (
          <span className="text-xs italic text-muted-foreground">—</span>
        ),
    },
    {
      key: "description",
      header: "Description",
      cellClassName: "max-w-[24rem]",
      cell: (te) => (
        <span className="block truncate text-sm text-muted-foreground" title={te.description ?? ""}>
          {te.description || "—"}
        </span>
      ),
    },
    {
      key: "hours",
      header: "Hours",
      headClassName: "w-24 text-right",
      cellClassName: "text-right tabular-nums",
      sortAccessor: (te) => Number(te.hours),
      cell: (te) => formatHours(Number(te.hours)),
    },
    {
      key: "approval",
      header: "State",
      headClassName: "w-28",
      sortAccessor: (te) => te.approvalState,
      // Approval only. Billing state is what the customer gets charged, which
      // is not this page's business and not this reader's decision.
      cell: (te) => (
        <Badge variant={APPROVAL_VARIANT[te.approvalState] ?? "outline"}>{te.approvalState}</Badge>
      ),
    },
  ];

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">My time</h1>
          <p className="text-muted-foreground mt-1">
            Everything you've logged, whichever way it got here — the timer, by hand, or a confirmed{" "}
            <Link to="/suggestions/time-entries" className="underline">
              time suggestion
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline">
            <a href={`/api/time-entries/export?${pdfQuery(search)}`} download>
              Download CSV
            </a>
          </Button>
          <Button asChild size="sm" variant="outline">
            <a href={`/api/time-entries/pdf?${pdfQuery(search)}`}>Download PDF</a>
          </Button>
          <Button asChild size="sm">
            <Link to="/time-entries/new">Log time</Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Filter by project"
          value={search.projectId ?? ""}
          onChange={(v) => setFilter({ projectId: v || undefined })}
          options={[
            { value: "", label: "All projects" },
            ...projects.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />

        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">From</span>
          <DatePicker
            aria-label="From date"
            className="w-36"
            value={search.from ? isoToPragueDate(search.from) : ""}
            onChange={(d) => setFilter({ from: d ? pragueWallToIso(`${d}T00:00`) : undefined })}
          />
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">To</span>
          <DatePicker
            aria-label="To date"
            className="w-36"
            value={search.to ? isoToPragueDate(search.to) : ""}
            onChange={(d) => setFilter({ to: d ? pragueWallToIso(`${d}T23:59`) : undefined })}
          />
        </div>

        {(search.projectId || search.from || search.to) && (
          <button
            type="button"
            data-testid="my-te-clear"
            onClick={() => navigate({ to: "/time-entries", search: {} })}
            className="h-9 px-2 text-sm text-muted-foreground underline hover:text-foreground"
          >
            Clear
          </button>
        )}

        <span className="ml-auto text-sm text-muted-foreground" data-testid="my-te-total">
          {formatHours(Number(totalHours))} h over {total} {total === 1 ? "entry" : "entries"}
        </span>
      </div>

      <DataTable
        columns={columns}
        rows={entries}
        rowKey={(te) => te.id}
        rowTestId={() => "my-te-row"}
        emptyState="Nothing logged in this range."
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="my-time-entries"
        basePath="/time-entries"
        searchParams={{
          projectId: search.projectId,
          from: search.from,
          to: search.to,
        }}
      />
    </div>
  );
}
